import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimpleIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { csvTemplateUrl, importCsv } from "@/lib/api";

export default function ImportCsvDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const r = await importCsv(file);
      setResult(r);
      toast.success(`Imported: ${r.created} created · ${r.updated} updated · ${r.skipped} skipped`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFile(null); setResult(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="import-csv-btn">
          <UploadSimpleIcon size={14} className="mr-2" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-xl" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Bulk Import Ingredients</DialogTitle>
          <DialogDescription>
            Upload a CSV. Existing ingredients (matched by name) are updated; new ones are created.
            Use <code className="font-mono text-xs">|</code> or <code className="font-mono text-xs">,</code> as separators for list fields
            (function, claims, ingredientTypes).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <a
            href={csvTemplateUrl}
            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--primary)] hover:underline"
            data-testid="csv-template-link"
          >
            <DownloadSimpleIcon size={14} /> Download CSV template
          </a>
          <div className="border border-dashed border-[var(--border)] p-6 text-center bg-[var(--surface)]">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full font-mono text-xs"
              data-testid="csv-file-input"
            />
            {file && <div className="mt-2 font-mono text-[11px] text-[var(--text-muted)]">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
          </div>
          {result && (
            <div className="border border-[var(--border)] p-3 font-mono text-xs" data-testid="csv-result">
              <div className="flex gap-4">
                <span>Created: <b>{result.created}</b></span>
                <span>Updated: <b>{result.updated}</b></span>
                <span>Skipped: <b>{result.skipped}</b></span>
              </div>
              {result.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[var(--danger)]">{result.errors.length} error(s)</summary>
                  <ul className="mt-1 max-h-40 overflow-auto text-[10px]">
                    {result.errors.map((er, i) => (
                      <li key={i}>Row {er.row}: {er.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none" data-testid="csv-cancel">Close</Button>
          <Button onClick={submit} disabled={!file || busy} className="btn-primary rounded-none" data-testid="csv-submit">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
